<script setup>
// Custom feature #6 module B — per-agent concurrent conversation cap.
// Mounted inside the OSS "Edit Agent" dialog (single-line injection). All logic
// and I/O live here; it talks to its own admin-only custom endpoint and never
// touches the OSS agent-update flow. Self-contained save button.
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

// Flip to true to trace user actions / responses in the browser console while
// debugging. Off by default so production stays quiet (no sensitive data logged).
const DEBUG_LOG = false;
const log = (...args) => {
  if (DEBUG_LOG) {
    // eslint-disable-next-line no-console
    console.info('[AgentConcurrencyLimit]', ...args);
  }
};

const DEFAULT_MAX = 9999;

const props = defineProps({
  agentId: { type: Number, required: true },
});

const { t } = useI18n();
const store = useStore();
const accountId = computed(() => store.state.route?.params?.accountId);

const loading = ref(true);
const saving = ref(false);
const errorMsg = ref('');
const savedMsg = ref('');

const enabled = ref(false);
const maxConcurrent = ref(DEFAULT_MAX);

const endpoint = () =>
  `/api/v1/accounts/${accountId.value}/agent_assignment_limits/${props.agentId}`;

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const { data } = await window.axios.get(endpoint());
    enabled.value = !!data.limit_enabled;
    maxConcurrent.value = data.max_concurrent || DEFAULT_MAX;
    log('loaded', data);
  } catch (err) {
    errorMsg.value = t('CUSTOM.COMMON.LOAD_FAILED');
    log('load failed', err);
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  saving.value = true;
  errorMsg.value = '';
  savedMsg.value = '';
  // A disabled cap or non-positive value means "no limit": normalize to default.
  const value = Number(maxConcurrent.value) > 0 ? Number(maxConcurrent.value) : DEFAULT_MAX;
  log('save click', { enabled: enabled.value, maxConcurrent: value });
  try {
    const { data } = await window.axios.put(endpoint(), {
      limit_enabled: enabled.value,
      max_concurrent: value,
    });
    maxConcurrent.value = data.max_concurrent || DEFAULT_MAX;
    savedMsg.value = t('CUSTOM.COMMON.SAVED');
    log('saved', data);
  } catch (err) {
    errorMsg.value = err?.response?.data?.errors?.join(', ') || t('CUSTOM.COMMON.SAVE_FAILED');
    log('save failed', err);
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <!-- Colors use the host's next-color CSS variables so it follows light/dark. -->
  <div class="acl-card">
    <label class="acl-switch">
      <input v-model="enabled" type="checkbox" :disabled="loading" />
      <span class="acl-switch-label">
        <strong>{{ t('CUSTOM.ASSIGNMENT.CONCURRENCY_TITLE') }}</strong>
        <em v-if="!enabled">{{ t('CUSTOM.ASSIGNMENT.CONCURRENCY_OFF_HINT') }}</em>
        <em v-else>
          {{ t('CUSTOM.ASSIGNMENT.CONCURRENCY_ON_HINT') }}
        </em>
      </span>
    </label>

    <div v-if="enabled" class="acl-row">
      <input
        v-model="maxConcurrent"
        type="number"
        min="1"
        class="acl-number"
        :disabled="loading"
      />
      <span class="acl-unit">{{ t('CUSTOM.ASSIGNMENT.CONCURRENCY_UNIT') }}</span>
    </div>

    <div class="acl-actions">
      <span v-if="savedMsg" class="acl-ok">{{ savedMsg }}</span>
      <span v-if="errorMsg" class="acl-err">{{ errorMsg }}</span>
      <button
        type="button"
        class="acl-btn"
        :disabled="loading || saving"
        @click="save"
      >
        {{ saving ? t('CUSTOM.COMMON.SAVING') : t('CUSTOM.ASSIGNMENT.SAVE_CONCURRENCY') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.acl-card {
  border: 1px solid rgb(var(--slate-5));
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
}
.acl-switch { display: flex; align-items: flex-start; gap: 12px; cursor: pointer; }
.acl-switch input { width: 18px; height: 18px; margin-top: 2px; cursor: pointer; accent-color: rgb(var(--blue-9)); }
.acl-switch-label { display: flex; flex-direction: column; gap: 3px; }
.acl-switch-label strong { font-size: 14px; color: rgb(var(--slate-12)); }
.acl-switch-label em { font-size: 12px; line-height: 1.6; color: rgb(var(--slate-11)); font-style: normal; }
.acl-row { display: flex; align-items: center; gap: 8px; margin-top: 12px; }
.acl-number {
  width: 120px; border: 1px solid rgb(var(--slate-7)); border-radius: 8px;
  padding: 8px 10px; font-size: 14px; color: rgb(var(--slate-12)); background: rgb(var(--slate-1));
}
.acl-unit { font-size: 13px; color: rgb(var(--slate-11)); }
.acl-actions { display: flex; align-items: center; justify-content: flex-end; gap: 12px; margin-top: 14px; }
.acl-btn {
  background: rgb(var(--blue-9)); color: #fff; border: none; border-radius: 8px;
  padding: 8px 18px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.acl-btn:disabled { opacity: .6; cursor: default; }
.acl-ok { color: rgb(var(--grass-11, 22 163 74)); font-size: 13px; }
.acl-err { color: rgb(var(--ruby-11, 220 38 38)); font-size: 13px; }
</style>
